import { useState } from "react";
import { Alert, Anchor, Box, Button, Container, Group, PasswordInput, SegmentedControl, SimpleGrid, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/http";
import { AccountButton } from "../components/AccountCta";
import { useSession } from "../lib/session";
import type { Me } from "../lib/types";
import { Logo } from "../components/AppLayout";
import { brand } from "../theme/brand";
import { Eyebrow, StatCard } from "../theme/components";

type Method = "invite" | "key";
const METHOD_KEY = "passio-demo.signin-method";

const keyParam = () => ["key", "apikey", "api-key"].includes(new URLSearchParams(window.location.search).get("signin") ?? "");

/** `?signin=key` (or a previous API-key sign-in in this browser) opens the API key tab. */
function initialMethod(): Method {
  if (keyParam()) return "key";
  return localStorage.getItem(METHOD_KEY) === "key" ? "key" : "invite";
}

/** Mirrors the selected tab in the URL so the page can be linked to either way. */
function setSigninParam(method: Method | null) {
  const url = new URL(window.location.href);
  if (method === "key") url.searchParams.set("signin", "key");
  else url.searchParams.delete("signin");
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
}

export function LoginPage({ notice, expired = false }: { notice?: string; expired?: boolean }) {
  const { setMe } = useSession();
  const [chosen, setChosen] = useState<Method>(initialMethod);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const options = useQuery({
    queryKey: ["auth-options"],
    queryFn: () => api<{ apiKeyLogin: boolean; selfSignup: boolean; environmentLabel: string; apiHost: string }>("GET", "/api/auth/options"),
    staleTime: 5 * 60_000,
  });
  const keyLoginOff = options.data?.apiKeyLogin === false;
  // A remembered choice falls back to invites when key sign-in is off; an explicit ?signin=key link still shows why.
  const method: Method = chosen === "key" && keyLoginOff && !keyParam() ? "invite" : chosen;
  const showTabs = !keyLoginOff || method === "key";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const me = method === "key" ? await api<Me>("POST", "/api/auth/key", { apiKey }) : await api<Me>("POST", "/api/auth/login", { username, password });
      localStorage.setItem(METHOD_KEY, method);
      setSigninParam(null);
      setMe(me);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in.");
      setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
    } finally {
      setBusy(false);
    }
  };

  const keyDisabled = method === "key" && keyLoginOff;
  const ended = errorCode === "account_expired" || (expired && !error);

  return (
    <Box className="pd-dark" mih="100vh">
      <Container size="lg" py={28}>
        <Logo />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={64} mt={{ base: 40, md: 90 }}>
          <Stack gap="lg">
            <Eyebrow>Passio Nutrition API · live demo</Eyebrow>
            <Title order={1} fz={{ base: 40, sm: 58 }} lh={1.05} c="white">
              {brand.tagline}
            </Title>
            <Text fz="lg" className="pd-dim" maw={520}>
              {brand.intro}
            </Text>
            <Group gap="sm">
              <AccountButton placement="login" size="md" />
              {options.data?.selfSignup && (
                <Button component={Link} to="/signup" size="md" variant="outline" color="gray.0" styles={{ root: { borderColor: "var(--pd-ink-border)" } }}>
                  Try the free demo
                </Button>
              )}
            </Group>
            <Group gap="md" mt="md" visibleFrom="md" align="stretch">
              <StatCard caption="photo · example" title="Chicken burrito bowl" stats={[{ value: "640", label: "kcal" }, { value: "38 g", label: "protein" }, { value: "1,420 mg", label: "sodium" }]} />
              <StatCard accent caption="example · sodium over target" title="Try the low-sodium version of that sauce" />
            </Group>
          </Stack>

          <Box p={{ base: 24, sm: 36 }} maw={440} w="100%" style={{ background: "#fff", borderRadius: 18, color: "var(--pd-ink)", justifySelf: "end" }}>
            <form onSubmit={submit}>
              <Stack gap="md">
                <Title order={2} fz={26}>
                  Sign in
                </Title>
                {showTabs && (
                  <SegmentedControl
                    fullWidth
                    value={method}
                    onChange={(v) => {
                      setChosen(v as Method);
                      setSigninParam(v as Method);
                      setError(null);
                    }}
                    data={[
                      { value: "invite", label: "Demo invite" },
                      { value: "key", label: "Passio API key" },
                    ]}
                  />
                )}
                {method === "key" ? (
                  <Text c="dimmed" fz="sm">
                    Already a Passio customer? Sign in with your API key and every call runs on your own account, with your plan, data and billing.
                    {options.data && (
                      <>
                        {" "}
                        This demo uses <b>{options.data.apiHost}</b>, so use a key for that environment.
                      </>
                    )}
                  </Text>
                ) : (
                  <Text c="dimmed" fz="sm">
                    Use the credentials from your invite email.
                  </Text>
                )}
                {notice && !error && <Alert color="orange">{notice}</Alert>}
                {error && <Alert color="red">{error}</Alert>}
                {ended && (
                  <Alert color="lime" title="Keep building with Passio">
                    <Stack gap="sm">
                      <Text fz="sm">Create your Passio account to get your own API keys and pick a plan.</Text>
                      <AccountButton placement="expired" color="ink" c="white" />
                    </Stack>
                  </Alert>
                )}
                {keyDisabled && <Alert color="orange">API key sign-in is turned off for this demo.</Alert>}
                {method === "key" ? (
                  <PasswordInput
                    label="API key"
                    description="Checked with the Passio API, then kept encrypted on the server for this session only."
                    value={apiKey}
                    onChange={(e) => setApiKey(e.currentTarget.value)}
                    autoComplete="off"
                    spellCheck={false}
                    required
                    autoFocus
                  />
                ) : (
                  <>
                    <TextInput label="Username" value={username} onChange={(e) => setUsername(e.currentTarget.value)} autoComplete="username" required autoFocus />
                    <PasswordInput label="Password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} autoComplete="current-password" required />
                  </>
                )}
                <Button type="submit" size="md" loading={busy} disabled={keyDisabled} fullWidth>
                  {method === "key" ? "Sign in with API key" : "Sign in"}
                </Button>
                {options.data?.selfSignup ? (
                  <Text fz="sm">
                    No account?{" "}
                    <Anchor component={Link} to="/signup" fw={600}>
                      Get a free demo account
                    </Anchor>
                  </Text>
                ) : (
                  <Text fz="xs" c="dimmed">
                    Need access? Contact {brand.supportEmail}.
                  </Text>
                )}
              </Stack>
            </form>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
